import React, { useState, useEffect } from 'react';
import api, { mediciAPI } from '../../services/api';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import IscrizioniAlbo from './IscrizioniAlbo';
import { useAuthStore } from '../../store/useStore';
import { pulisciCodice, validaDocumento } from '../../utils/codiceFiscale';
import { notificaErrore, messaggioDa } from '../../utils/notifiche';
// La tavolozza del colore in agenda sta in un file suo: la usa anche la
// «Modifica» dell'agenda negli Orari, che scrive lo stesso campo.
import { COLORI_AGENDA } from '../../utils/coloriAgenda';
import '../../styles/MedicoForm.css';

/* Li cambia solo l'amministrazione (CAMPI_SOLO_ADMIN di mediciController):
   per gli altri ruoli la scheda li mostra spenti e non li manda. */
export const CAMPI_SOLO_ADMIN = [
  'percentuale_spettanza', 'ritenuta_acconto', 'ritenuta_aliquota',
  'registro_rx', 'quota_struttura_rx_percentuale',
  'estrazione_automatica_esami', 'sigla',
];

const MedicoForm = ({ medico, onSave, onCancel }) => {
  /* I valori gia' in uso, per la tendina: si sceglie fra quelli che
     esistono invece di riscriverne uno con una maiuscola diversa e
     creare un gruppo nuovo con dentro una persona sola. */
  const [specEsistenti, setSpecEsistenti] = useState([]);
  useEffect(() => {
    mediciAPI.getAll()
      .then((r) => {
        const s = new Set();
        (r.data?.data?.medici || []).forEach((m) => {
          (m.specializzazioni || []).forEach((x) => s.add(x));
          if (m.specializzazione) s.add(m.specializzazione);
        });
        setSpecEsistenti([...s].sort((a, b) => a.localeCompare(b, 'it')));
      })
      .catch(() => {});   // la tendina e' un aiuto, non un requisito
  }, []);

  /* I campi di CAMPI_SOLO_ADMIN (spettanza, ritenuta, registro RX, estrazione
     degli esami, sigla) li cambia solo l'amministratore: agli altri si
     mostrano spenti e non partono col salvataggio; il server rifiuta col 403
     un valore diverso da quello salvato (Alex, 21/10/2026). */
  const ruolo = useAuthStore((s) => s.user?.ruolo);
  const puoSpettanza = ruolo === 'admin';
  const isEdit = Boolean(medico && medico.id);
  /* «Crea anche la sua agenda» (Alex, 4/10/2026): un medico nuovo senza
     agenda non compare nella griglia, e fino al 6/10 non c'era nessuna
     schermata che gliela desse. Accesa di serie: chi la spegne lo decide. */
  const [creaAgenda, setCreaAgenda] = useState(true);

  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    sigla: '',
    codice_fiscale: '',
    partita_iva: '',
    specializzazione: '',
    /* L'elenco strutturato, accanto alla stringa. Sono due cose diverse
       e la schermata lo dice: la stringa e' cio' che si STAMPA sui
       referti e sui consensi, l'elenco e' cio' su cui si FILTRA. */
    specializzazioni: [],
    /* Le iscrizioni all'albo: un elenco, non un numero (migration 190). */
    iscrizioni: [],
    email: '',
    telefono: '',
    percentuale_spettanza: '',
    assicurazione_compagnia: '',
    assicurazione_numero_polizza: '',
    assicurazione_data_scadenza: '',
    note: '',
    colore: '',
    ritenuta_acconto: false,
    registro_rx: false,
    quota_struttura_rx_percentuale: '',
    ritenuta_aliquota: '',
    percorso_longevity: false,
    estrazione_automatica_esami: false,
    schede: [],
    attivo: true,
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  /* Chiudere con qualcosa di diverso da com'era chiede conferma: clic
     sullo sfondo, X e «Annulla» passano tutti da annulla(). */
  const [versione, setVersione] = useState(0);
  const sporco = useSporco(formData, versione);
  const chiedi = useUscitaProtetta(sporco);
  const annulla = () => { if (chiedi()) onCancel(); };
  const [generalError, setGeneralError] = useState(null);

  // Popola form se in modifica
  /* Le schede che la struttura ha composto. Si chiedono una volta
     all'apertura: sono cinque righe e non cambiano mentre si compila un
     modulo. Se la richiesta fallisce l'elenco resta vuoto e il riquadro
     lo dice, invece di mostrare una tendina muta. */
  const [schedeDisponibili, setSchedeDisponibili] = useState([]);
  useEffect(() => {
    api.get('/schede')
      .then((r) => setSchedeDisponibili(r.data?.data?.schede || []))
      .catch(() => setSchedeDisponibili([]));
  }, []);

  /* QUELLO CHE NON SI E' CARICATO NON SI SALVA.
     L'elenco dei medici (GET /medici) non porta le schede cliniche: le
     porta solo GET /medici/:id. Aprendo «Modifica» dall'elenco il modulo
     trovava `medico.schede` non definito, mostrava tutte le caselle
     spente e al salvataggio mandava una lista VUOTA - che per il server
     e' una scelta, e cancellava le schede del medico. Dal 13 settembre
     2026 (bf6447d) e' andata cosi' per 38 medici su 40, in silenzio:
     medico_schede non ha nemmeno un trigger di audit.
     Qui il record si completa prima di popolare il modulo. `schedeCaricate`
     copre le strade che non passano di qui: senza schede caricate la
     chiave non viene proprio spedita, e il server lascia stare quello che
     c'e' (salvaSchede: `undefined` = «non me ne sto occupando»). */
  const [record, setRecord] = useState(medico);
  const schedeCaricate = Array.isArray(record?.schede);

  useEffect(() => {
    setRecord(medico);
    if (!isEdit || Array.isArray(medico.schede)) return undefined;
    let vivo = true;
    mediciAPI.getById(medico.id)
      .then((r) => {
        const completo = r.data?.data?.medico;
        if (vivo && completo) setRecord(completo);
      })
      .catch(() => { /* si resta su cio' che si ha: senza schede caricate non le tocca */ });
    return () => { vivo = false; };
  }, [medico, isEdit]);

  useEffect(() => {
    if (isEdit) {
      setFormData({
        nome: record.nome || '',
        cognome: record.cognome || '',
        sigla: record.sigla || '',
        codice_fiscale: record.codice_fiscale || '',
        partita_iva: record.partita_iva || '',
        specializzazione: record.specializzazione || '',
        specializzazioni: record.specializzazioni
            || (record.specializzazione ? [record.specializzazione] : []),
        iscrizioni: (record.iscrizioni || []).map((i) => ({
          albo_codice: i.albo_codice || '', sede_id: i.sede_id || '', numero: i.numero || '',
          sezione: i.sezione || '', data_iscrizione: i.data_iscrizione || '', principale: !!i.principale,
        })),
        email: record.email || '',
        telefono: record.telefono || '',
        percentuale_spettanza:
          record.percentuale_spettanza != null ? record.percentuale_spettanza : '',
        assicurazione_compagnia: record.assicurazione_compagnia || '',
        assicurazione_numero_polizza: record.assicurazione_numero_polizza || '',
        assicurazione_data_scadenza: record.assicurazione_data_scadenza
          ? record.assicurazione_data_scadenza.split('T')[0]
          : '',
        note: record.note || '',
        colore: record.colore || '',
        ritenuta_acconto: record.ritenuta_acconto === true,
        registro_rx: record.registro_rx === true,
        quota_struttura_rx_percentuale:
          record.quota_struttura_rx_percentuale != null ? record.quota_struttura_rx_percentuale : '',
        percorso_longevity: record.percorso_longevity === true,
        estrazione_automatica_esami: record.estrazione_automatica_esami === true,
        schede: Array.isArray(record.schede) ? record.schede : [],
        ritenuta_aliquota:
          record.ritenuta_aliquota != null ? record.ritenuta_aliquota : '',
        attivo: record.attivo !== false,
      });
    }
    setVersione((v) => v + 1);
  }, [record, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Pulisci errore campo
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Obbligatori
    if (!formData.nome.trim()) newErrors.nome = 'Il nome è obbligatorio';
    if (!formData.cognome.trim()) newErrors.cognome = 'Il cognome è obbligatorio';

    /* Il codice fiscale con la regola del server (mediciController,
       rifiutoCodiceMedico), e solo se e' CAMBIATO rispetto a quello caricato
       (quarta revisione del beta su 4cbe4cb, 7/10/2026). Il server giudica
       solo un codice che cambia, perche' la scheda manda tutto il modulo e un
       medico in archivio ha un codice di prima non valido; qui la forma
       (/^[A-Z0-9]{16}$/) bloccava lo stesso il salvataggio di ogni altro
       campo. Il confronto e' quello del server: il codice pulito contro
       quello salvato. */
    const cf = pulisciCodice(formData.codice_fiscale);
    const cfCambiato = !isEdit || cf !== (record?.codice_fiscale || '');
    if (cf && cfCambiato) {
      const esito = validaDocumento(cf, 'cf_italiano');
      if (!esito.valido) newErrors.codice_fiscale = esito.errore;
    }

    // La sigla: tre lettere (migration 278), la sceglie l'amministrazione
    if (formData.sigla.trim() && !/^[A-Za-z]{3}$/.test(formData.sigla.trim())) {
      newErrors.sigla = 'La sigla del medico è di tre lettere maiuscole, per esempio ROS.';
    }

    // Partita IVA (11 cifre)
    if (formData.partita_iva.trim()) {
      if (!/^\d{11}$/.test(formData.partita_iva.trim())) {
        newErrors.partita_iva = 'La P.IVA deve essere di 11 cifre';
      }
    }

    // Email
    if (formData.email.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        newErrors.email = 'Email non valida';
      }
    }

    // Percentuale spettanza
    if (formData.percentuale_spettanza !== '' && formData.percentuale_spettanza !== null) {
      const perc = parseFloat(formData.percentuale_spettanza);
      if (isNaN(perc) || perc < 0 || perc > 100) {
        newErrors.percentuale_spettanza = 'Deve essere un numero tra 0 e 100';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError(null);

    if (!validate()) return;

    setSaving(true);
    try {
      // Prepara payload pulito
      const payload = {
        ...formData,
        nome: formData.nome.trim(),
        cognome: formData.cognome.trim(),
        sigla: formData.sigla.trim().toUpperCase() || null,
        codice_fiscale: pulisciCodice(formData.codice_fiscale) || null,
        partita_iva: formData.partita_iva.trim() || null,
        specializzazione: formData.specializzazione.trim() || null,
        iscrizioni: formData.iscrizioni,
        email: formData.email.trim() || null,
        telefono: formData.telefono.trim() || null,
        percentuale_spettanza:
          formData.percentuale_spettanza !== ''
            ? parseFloat(formData.percentuale_spettanza)
            : null,
        assicurazione_compagnia: formData.assicurazione_compagnia.trim() || null,
        assicurazione_numero_polizza: formData.assicurazione_numero_polizza.trim() || null,
        assicurazione_data_scadenza: formData.assicurazione_data_scadenza || null,
        note: formData.note.trim() || null,
        colore: formData.colore || null,
        ritenuta_acconto: !!formData.ritenuta_acconto,
        percorso_longevity: !!formData.percorso_longevity,
        estrazione_automatica_esami: !!formData.estrazione_automatica_esami,
        ...(schedeCaricate || !isEdit ? { schede: formData.schede } : {}),
        ritenuta_aliquota:
          formData.ritenuta_aliquota !== '' ? parseFloat(formData.ritenuta_aliquota) : null,
        registro_rx: !!formData.registro_rx,
        quota_struttura_rx_percentuale:
          formData.quota_struttura_rx_percentuale !== '' ? parseFloat(formData.quota_struttura_rx_percentuale) : null,
      };
      /* I campi che solo l'amministrazione cambia NON partono da chi non la
         e' (Alex, 21/10/2026: «Un campo che il ruolo non vede NON si manda
         dal modulo»). Il server li tratta cosi': assente non tocca, presente
         e diverso da quello salvato e' 403. Mandarli coi valori di serie (alla
         creazione) o coi valori caricati obbligava il server a indovinare. */
      if (!puoSpettanza) {
        for (const k of CAMPI_SOLO_ADMIN) delete payload[k];
      }

      if (isEdit) {
        await mediciAPI.update(medico.id, payload);
      } else {
        const creato = await mediciAPI.create(payload);
        const nuovoId = creato?.data?.data?.medico?.id;
        /* L'agenda nasce dalla stessa rotta di «Nuova agenda» negli Orari,
           con il colore scelto qui (alla creazione il colore non ha altro
           posto dove andare). Il medico ormai c'e': se l'agenda non nasce
           la finestra si chiude lo stesso, e il motivo resta a schermo
           finche' non lo si chiude, con la strada per rimediare. */
        if (creaAgenda && nuovoId) {
          try {
            await api.post('/agenda/orari/agende', {
              tipo: 'medico', medico_id: nuovoId, colore: formData.colore || null,
            });
          } catch (errAgenda) {
            notificaErrore(`Il medico è salvato, ma la sua agenda no: ${messaggioDa(errAgenda, 'errore nella creazione dell’agenda.')} Puoi crearla da Orari, con «Nuova agenda».`);
          }
        }
      }

      onSave();
    } catch (err) {
      console.error('Errore salvataggio medico:', err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Errore durante il salvataggio del medico';
      setGeneralError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('modal-overlay') && !saving) {
      annulla();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content medico-form-modal">
        <div className="modal-header">
          <h2>{isEdit ? 'Modifica medico' : 'Nuovo medico'}</h2>
          <button
            type="button"
            className="btn-close"
            onClick={annulla}
            disabled={saving}
            aria-label="Chiudi"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="medico-form" noValidate>
          {generalError && <div className="error-message general">{generalError}</div>}

          {/* SEZIONE ANAGRAFICA */}
          <fieldset className="form-section">
            <legend>Dati anagrafici</legend>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nome">
                  Nome <span className="required">*</span>
                </label>
                <input
                  id="nome"
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  className={errors.nome ? 'error' : ''}
                  disabled={saving}
                  autoFocus
                />
                {errors.nome && <span className="field-error">{errors.nome}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="cognome">
                  Cognome <span className="required">*</span>
                </label>
                <input
                  id="cognome"
                  type="text"
                  name="cognome"
                  value={formData.cognome}
                  onChange={handleChange}
                  className={errors.cognome ? 'error' : ''}
                  disabled={saving}
                />
                {errors.cognome && <span className="field-error">{errors.cognome}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="sigla">Sigla</label>
                <input
                  id="sigla"
                  type="text"
                  name="sigla"
                  value={formData.sigla}
                  onChange={handleChange}
                  className={errors.sigla ? 'error' : ''}
                  maxLength={3}
                  style={{ textTransform: 'uppercase' }}
                  disabled={saving || !puoSpettanza}
                  placeholder="ROS"
                />
                {errors.sigla && <span className="field-error">{errors.sigla}</span>}
                <small className="form-hint">
                  Tre lettere, uniche: nel foglio del catalogo il medico si sceglie così,
                  mai col codice fiscale. Lasciata vuota, la genera il gestionale.
                  {!puoSpettanza && ' La cambia l\'amministrazione.'}
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="codice_fiscale">Codice fiscale</label>
                <input
                  id="codice_fiscale"
                  type="text"
                  name="codice_fiscale"
                  value={formData.codice_fiscale}
                  onChange={handleChange}
                  className={errors.codice_fiscale ? 'error' : ''}
                  /* Niente maxLength (7/10/2026): il browser taglia il testo
                     grezzo, prima di pulisciCodice, e un codice incollato a
                     gruppi arrivava corto. La lunghezza la dice il controllo
                     qui sopra, sul codice pulito, e la rifiuta il server. */
                  style={{ textTransform: 'uppercase' }}
                  disabled={saving}
                />
                {errors.codice_fiscale && (
                  <span className="field-error">{errors.codice_fiscale}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="partita_iva">Partita IVA</label>
                <input
                  id="partita_iva"
                  type="text"
                  name="partita_iva"
                  value={formData.partita_iva}
                  onChange={handleChange}
                  className={errors.partita_iva ? 'error' : ''}
                  maxLength={11}
                  disabled={saving}
                />
                {errors.partita_iva && (
                  <span className="field-error">{errors.partita_iva}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* SEZIONE PROFESSIONALE */}
          <fieldset className="form-section">
            <legend>Dati Professionali</legend>

            {/* I due campi stanno uno accanto all'altro e sembrano lo
                stesso dato: chi li vede vicini pensa che modificarne uno
                cambi l'altro. Questa riga esiste per dire che no, e sta
                SOPRA tutti e due invece che sotto uno dei due. */}
            <p className="form-avviso-doppio">
              <b>Sono due campi separati e si modificano separatamente.</b> Quello
              a sinistra è ciò che <b>si stampa</b> sui documenti; quello a destra
              serve a <b>trovare</b> il medico nei filtri. Cambiarne uno non
              cambia l&apos;altro.
            </p>

            <div className="form-row">
              <div className="form-group flex-2">
                <label htmlFor="specializzazione">Specializzazione stampata</label>
                <input
                  id="specializzazione"
                  type="text"
                  name="specializzazione"
                  value={formData.specializzazione}
                  onChange={handleChange}
                  placeholder="Es: Medicina Estetica, Dermatologia..."
                  disabled={saving}
                />
                <small className="form-nota">
                  Compare <b>sotto il nome</b> nei referti, nelle fatture e nei
                  consensi che il paziente firma, accanto al numero d&apos;ordine.
                  È l&apos;unica che si stampa: se ne scrive una sola, quella che il
                  medico sceglie per sé.
                </small>
              </div>

              <div className="form-group flex-2">
                <label htmlFor="spec-nuova">Specializzazioni per la ricerca</label>
                {/* Una per riga, invece di una stringa con la barra. Con la
                    barra chi ne ha due non compare sotto nessuna delle due:
                    il filtro confronta la stringa intera, e «Fisiatria/
                    Medicina Estetica» e' un gruppo suo con dentro una
                    persona sola. */}
                <div className="spec-elenco">
                  {formData.specializzazioni.map((x) => (
                    <span key={x} className="spec-voce">
                      {x}
                      <button type="button" disabled={saving}
                              title={`Togli ${x}`}
                              onClick={() => setFormData((f) => ({
                                ...f,
                                specializzazioni: f.specializzazioni.filter((y) => y !== x),
                              }))}>×</button>
                    </span>
                  ))}
                  {formData.specializzazioni.length === 0 && (
                    <span className="spec-vuoto">
                      Nessuna: questo medico non comparirà in nessun filtro.
                    </span>
                  )}
                </div>
                <input
                  id="spec-nuova"
                  type="text"
                  list="spec-esistenti"
                  placeholder="Aggiungi e premi Invio"
                  disabled={saving}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    /* Invio qui dentro NON manda il modulo: si sta
                       aggiungendo una voce, non salvando il medico. */
                    e.preventDefault();
                    const v = e.target.value.trim();
                    if (!v) return;
                    setFormData((f) => (f.specializzazioni.includes(v) ? f : {
                      ...f, specializzazioni: [...f.specializzazioni, v],
                    }));
                    e.target.value = '';
                  }}
                />
                {/* Si sceglie fra quelle che esistono gia': «Dermatologia»
                    riscritta con la d minuscola creerebbe un gruppo nuovo
                    con dentro una persona sola. */}
                <datalist id="spec-esistenti">
                  {specEsistenti.map((x) => <option key={x} value={x} />)}
                </datalist>
                <small className="form-nota">
                  Servono a <b>trovare</b> il medico nei filtri e nelle ricerche,
                  e possono essere più d&apos;una. <b>Non si stampano da nessuna
                  parte.</b>
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="percentuale_spettanza">% Spettanza</label>
                <input
                  id="percentuale_spettanza"
                  type="number"
                  name="percentuale_spettanza"
                  value={formData.percentuale_spettanza}
                  onChange={handleChange}
                  className={errors.percentuale_spettanza ? 'error' : ''}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="Es: 60"
                  disabled={saving || !puoSpettanza}
                />
                {!puoSpettanza && (
                  <small className="campo-nota">
                    La cambia l&apos;amministrazione: è quanto spetta al medico,
                    non un dato dell&apos;anagrafica.
                  </small>
                )}
                {errors.percentuale_spettanza && (
                  <span className="field-error">{errors.percentuale_spettanza}</span>
                )}
              </div>

              <div className="form-group">
                <label>Colore in agenda</label>
                <div className="colore-agenda-swatches">
                  {COLORI_AGENDA.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`colore-swatch ${formData.colore === c ? 'scelto' : ''}`}
                      style={{ background: c }}
                      onClick={() => setFormData((prev) => ({ ...prev, colore: c }))}
                      disabled={saving}
                      aria-label={`Colore ${c}`}
                      title={c}
                    />
                  ))}
                  <button
                    type="button"
                    className={`colore-swatch nessuno ${!formData.colore ? 'scelto' : ''}`}
                    onClick={() => setFormData((prev) => ({ ...prev, colore: '' }))}
                    disabled={saving}
                    aria-label="Nessun colore"
                    title="Nessuno (grigio di default)"
                  >
                    ✕
                  </button>
                </div>
                <small className="colore-agenda-nota">
                  Identifica la colonna del medico nella griglia dell&apos;agenda.
                </small>
              </div>

              {!isEdit && (
                <div className="form-group">
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      name="crea_agenda"
                      data-crea-agenda
                      checked={creaAgenda}
                      onChange={(e) => setCreaAgenda(e.target.checked)}
                      disabled={saving}
                    />
                    <span>Crea anche la sua agenda</span>
                  </label>
                  <small className="colore-agenda-nota">
                    La colonna del medico in agenda, col colore scelto qui. Gli
                    orari si impostano poi da Orari.
                  </small>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-inline">
                  <input
                    type="checkbox"
                    name="ritenuta_acconto"
                    checked={formData.ritenuta_acconto}
                    onChange={handleChange}
                    disabled={saving || !puoSpettanza}
                  />
                  Soggetto a ritenuta d&apos;acconto
                </label>
                {formData.ritenuta_acconto && (
                  <div className="ritenuta-aliquota">
                    <label htmlFor="ritenuta_aliquota">Aliquota %</label>
                    <input
                      id="ritenuta_aliquota"
                      type="number"
                      name="ritenuta_aliquota"
                      value={formData.ritenuta_aliquota}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="20"
                      disabled={saving || !puoSpettanza}
                    />
                  </div>
                )}
                <small className="colore-agenda-nota">
                  La clinica trattiene questa quota sul compenso e la versa
                  all&apos;Agenzia; nel riepilogo spettanze si scala dal netto.
                </small>
                {!puoSpettanza && (
                  <small className="campo-nota">
                    La cambia l&apos;amministrazione, come la percentuale di
                    spettanza: è una trattenuta sul compenso di una persona.
                  </small>
                )}
              </div>

              {/* Registro RX. L'interruttore sta qui e non sulla scheda del
                  paziente perche' l'abilitazione e' del MEDICO: un medico non
                  se la accende da solo, e il server riserva questa rotta ad
                  amministrazione e segreteria. */}
              <div className="form-group">
                <label className="checkbox-inline">
                  <input
                    type="checkbox"
                    name="registro_rx"
                    checked={formData.registro_rx}
                    onChange={handleChange}
                    disabled={saving || !puoSpettanza}
                  />
                  Registro RX
                </label>
                {formData.registro_rx && (
                  <div className="ritenuta-aliquota">
                    <label htmlFor="quota_struttura_rx_percentuale">Quota alla struttura %</label>
                    <input
                      id="quota_struttura_rx_percentuale"
                      type="number"
                      name="quota_struttura_rx_percentuale"
                      value={formData.quota_struttura_rx_percentuale}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="15"
                      disabled={saving || !puoSpettanza}
                    />
                  </div>
                )}
                <small className="colore-agenda-nota">
                  Per chi fattura in proprio certe prestazioni e riconosce una
                  quota alla struttura. Acceso, il medico trova il tasto RX sulla
                  scheda dei pazienti e vede solo le proprie righe. La percentuale
                  si congela su ogni riga quando viene scritta: cambiarla qui non
                  riscrive il passato. Questi importi non entrano in fatturazione
                  n&eacute; nelle spettanze.
                </small>
                {!puoSpettanza && (
                  <small className="campo-nota">
                    Registro RX e quota li cambia l&apos;amministrazione.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="checkbox-inline">
                  <input
                    type="checkbox"
                    name="percorso_longevity"
                    checked={formData.percorso_longevity}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  Segue il percorso longevity
                </label>
                <small className="colore-agenda-nota">
                  Vede il riquadro con i dati del braccialetto nelle schede dei
                  pazienti che lo hanno collegato. Per gli altri medici il
                  riquadro non compare.
                </small>
              </div>

              {/* La spunta «Ha anche la cartella di medicina estetica» non
                  c'e' piu' dal 22 settembre 2026: le procedure del paziente
                  sono un elenco solo, e la spunta non decideva piu' niente
                  (la usavano 0 medici su 41). La colonna vede_estetica resta
                  nel database e non si scrive da qui. */}
              {/* Su una riga sua, larga tutta la finestra: e' una riga di
                  quattro campi piu' un selettore, e in una colonna su tre
                  le intestazioni si impastavano (22 settembre 2026). */}
              <div className="form-group form-group-full">
                <label>Iscrizioni all'albo</label>
                <IscrizioniAlbo
                  valore={formData.iscrizioni}
                  onChange={(iscrizioni) => setFormData((f) => ({ ...f, iscrizioni }))}
                  disabilitato={saving}
                />
              </div>
            </div>

            {/* LE SCHEDE CLINICHE DI QUESTO MEDICO.
                Non e' riservatezza: la cartella resta aperta e i dati li legge
                chiunque. E' il menu - la scheda di ginecologia nel profilo di
                un ortopedico sono nove voci da scorrere per trovarne una. */}
            <div className="form-group form-group-full">
              <span className="schede-titolo">Schede cliniche</span>
              {/* Caselle da spuntare, non un elenco a selezione multipla:
                  quello pretendeva Ctrl (o Cmd) per prenderne piu' d'una, e
                  chi non lo sapeva - cioe' quasi tutti - a ogni clic
                  perdeva la scelta precedente senza capire perche'. Con le
                  caselle si vede cosa e' acceso senza doverlo provare. */}
              {schedeDisponibili.length > 0 && (
                <div className="schede-caselle">
                  {schedeDisponibili.map((s) => {
                    const scelta = formData.schede.map(Number).includes(Number(s.id));
                    return (
                      <label key={s.id} className="schede-casella">
                        <input
                          type="checkbox"
                          checked={scelta}
                          disabled={saving}
                          onChange={() => setFormData({
                            ...formData,
                            schede: scelta
                              ? formData.schede.filter((x) => Number(x) !== Number(s.id))
                              : [...formData.schede.map(Number), Number(s.id)],
                          })}
                        />
                        <span>
                          {s.nome}
                          {s.area_nome && <em> — {s.area_nome}</em>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {schedeDisponibili.length > 1 && (
                <div className="schede-scorciatoie">
                  <button type="button" disabled={saving}
                          onClick={() => setFormData({
                            ...formData,
                            schede: schedeDisponibili.map((s) => Number(s.id)),
                          })}>
                    Tutte
                  </button>
                  <button type="button" disabled={saving}
                          onClick={() => setFormData({ ...formData, schede: [] })}>
                    Nessuna
                  </button>
                </div>
              )}
              <small className="colore-agenda-nota">
                {schedeDisponibili.length ? (
                  <>
                    Quali schede compaiono nel profilo medico dei pazienti, per
                    questo medico. <b>Non &egrave; una restrizione sui
                    dati</b>: quello che un collega ha scritto resta leggibile a
                    tutti, come sempre. Toglie solo le voci che a questo medico
                    non servono. Senza nessuna spunta non compare nessuna scheda.
                  </>
                ) : (
                  <>
                    Nessuna scheda clinica &egrave; ancora stata composta. I campi
                    ci sono in catalogo; la scheda si compone da
                    <b> Schede cliniche</b> nel menu.
                  </>
                )}
              </small>
            </div>
          </fieldset>

          {/* SEZIONE CONTATTI */}
          <fieldset className="form-section">
            <legend>Contatti</legend>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  disabled={saving}
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="telefono">Telefono</label>
                <input
                  id="telefono"
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
            </div>
          </fieldset>

          {/* SEZIONE ASSICURAZIONE */}
          <fieldset className="form-section">
            <legend>Assicurazione Professionale</legend>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="assicurazione_compagnia">Compagnia</label>
                <input
                  id="assicurazione_compagnia"
                  type="text"
                  name="assicurazione_compagnia"
                  value={formData.assicurazione_compagnia}
                  onChange={handleChange}
                  placeholder="Es: Generali, AIG..."
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="assicurazione_numero_polizza">N° Polizza</label>
                <input
                  id="assicurazione_numero_polizza"
                  type="text"
                  name="assicurazione_numero_polizza"
                  value={formData.assicurazione_numero_polizza}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="assicurazione_data_scadenza">Data Scadenza</label>
                <input
                  id="assicurazione_data_scadenza"
                  type="date"
                  name="assicurazione_data_scadenza"
                  value={formData.assicurazione_data_scadenza}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
            </div>
          </fieldset>

          {/* SEZIONE NOTE */}
          <fieldset className="form-section">
            <legend>Note e Stato</legend>

            <div className="form-group">
              <label htmlFor="note">Note</label>
              <textarea
                id="note"
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows={3}
                placeholder="Note interne sul medico..."
                disabled={saving}
              />
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="attivo" className="checkbox-label">
                <input
                  id="attivo"
                  type="checkbox"
                  name="attivo"
                  checked={formData.attivo}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span>Medico attivo</span>
              </label>
            </div>

            {/* Migration 267: con l'opzione accesa, un referto di laboratorio
                caricato per questo medico viene letto da solo; i valori
                restano da verificare. Il medico la cambia anche dal suo
                profilo; ogni cambio sta nel registro modifiche. */}
            <div className="form-group checkbox-group">
              <label htmlFor="estrazione_automatica_esami" className="checkbox-label">
                <input
                  id="estrazione_automatica_esami"
                  type="checkbox"
                  name="estrazione_automatica_esami"
                  checked={formData.estrazione_automatica_esami}
                  onChange={handleChange}
                  disabled={saving || !puoSpettanza}
                />
                <span>Estrazione automatica degli esami</span>
              </label>
            </div>
          </fieldset>

          {/* AZIONI */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={annulla}
              disabled={saving}
            >
              Annulla
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Salvataggio...' : isEdit ? 'Salva modifiche' : 'Crea Medico'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicoForm;
